import type { TeamListDto } from '../services/teams/teamService';

export interface ResponsibleConsultantDropdownOption {
  key: string;
  value: string;
  text: string;
  name?: string;
  usuarioVtme?: string | null;
}

const normalizeUserId = (id: string) => (id || '').toLowerCase();

export function formatConsultantDisplayName(
  name: string,
  usuarioVtme?: string | null,
  extraSuffix?: string,
): string {
  const trimmedVtme = (usuarioVtme ?? '').trim();
  const vtmePart = trimmedVtme ? ` (${trimmedVtme})` : '';
  const suffix = extraSuffix ? ` ${extraSuffix}` : '';
  return `${name}${vtmePart}${suffix}`;
}

export function formatConsultantUserDisplayName(user?: {
  nome?: string | null;
  username?: string | null;
  usuarioVtme?: string | null;
} | null): string {
  const name = (user?.nome || user?.username || '').trim();
  if (!name) return 'N/A';
  return formatConsultantDisplayName(name, user?.usuarioVtme);
}

export function resolveConsultantDisplayLabel(
  user?: {
    id?: string | null;
    nome?: string | null;
    username?: string | null;
    usuarioVtme?: string | null;
  } | null,
  userId?: string | null,
  options?: ResponsibleConsultantDropdownOption[],
): string {
  const resolvedUserId = (user?.id || userId || '').trim();
  if (resolvedUserId && options?.length) {
    const fromOption = options.find(
      (option) => option.value.toLowerCase() === resolvedUserId.toLowerCase(),
    );
    if (fromOption?.text) return fromOption.text;
  }
  return formatConsultantUserDisplayName(user);
}

/**
 * Vendedores ativos + líder do time (managerId) para o campo consultor responsável.
 * Inclui times de treinamento — participantes também geram contratos.
 */
export function buildResponsibleConsultantDropdownOptions(
  teams: TeamListDto[],
  options: { isAdmin: boolean; currentUserId: string },
): ResponsibleConsultantDropdownOption[] {
  const seen = new Set<string>();
  const opts: ResponsibleConsultantDropdownOption[] = [];

  const addOption = (
    userId: string,
    name: string,
    usuarioVtme?: string | null,
    extraSuffix?: string,
  ) => {
    const normalized = normalizeUserId(userId);
    if (!userId || seen.has(normalized)) return;
    seen.add(normalized);
    opts.push({
      key: userId,
      value: userId,
      text: formatConsultantDisplayName(name, usuarioVtme, extraSuffix),
      name,
      usuarioVtme: (usuarioVtme ?? '').trim() || undefined,
    });
  };

  const addTeamMembers = (team: TeamListDto) => {
    if (team.managerId && team.managerName) {
      addOption(team.managerId, team.managerName, team.managerUsuarioVtme, '(Líder)');
    }
    for (const member of team.salespersons || []) {
      if (member.isActive) {
        addOption(member.userId, member.userName, member.usuarioVtme);
      }
    }
  };

  if (options.isAdmin) {
    for (const team of teams) {
      addTeamMembers(team);
    }
  } else {
    const managedTeam = teams.find(
      (t) => normalizeUserId(t.managerId) === normalizeUserId(options.currentUserId),
    );
    if (managedTeam) {
      addTeamMembers(managedTeam);
    }
  }

  opts.sort((a, b) => a.text.localeCompare(b.text, 'pt-BR'));
  return opts;
}
